import { useState } from 'react';
import { ArrowRight, Eye, EyeOff } from 'lucide-react';
import { Button } from '@/shared/components/Button';
import { FormField } from '@/shared/components/FormField';
import { Icon } from '@/shared/components/Icon';
import { Input } from '@/shared/components/Input';
import { Logo } from '@/shared/components/Logo';
import { useLoginViewModel } from '../viewmodels/useLoginViewModel';
import { ForgotPasswordModal } from './ForgotPasswordModal';
import { LoginNoticeCard } from './LoginNoticeCard';
import { TwoFactorPanel } from './TwoFactorPanel';
import styles from './LoginView.module.scss';

/**
 * Tela de login.
 *
 * Reproduz o desenho do sistema atual — fundo marinho com grafismo, card
 * dividido em painel de marca (gradiente) e formulário — sobre os tokens do
 * design system. Abaixo de `md` o painel de marca sai e sobra o formulário,
 * com a logo acima.
 *
 * A View não conhece a API: todo estado e toda decisão vêm do ViewModel.
 */
export function LoginView() {
  const vm = useLoginViewModel();
  const [senhaVisivel, setSenhaVisivel] = useState(false);

  const emailDigitado = vm.form.watch('email');

  return (
    <main className={styles.page}>
      <div className={styles.backdrop} aria-hidden="true">
        <img src="/images/background-mms.svg" alt="" className={styles.pageGraphic} />
      </div>

      <div className={styles.card}>
        <aside className={styles.brand} aria-hidden="true">
          <Logo width={383} variant="invert" className={styles.brandLogo} />
        </aside>

        <section className={styles.panel}>
          <div className={styles.column}>
            <Logo width={132} className={styles.mobileLogo} />

            {vm.challenge ? (
              <TwoFactorPanel
                challenge={vm.challenge}
                onAuthenticated={vm.completeChallenge}
                onCancel={vm.cancelChallenge}
              />
            ) : (
                <>
                  <header className={styles.header}>
                    <h1 className={styles.welcome}>Bem-vindo!</h1>
                    <div className={styles.tabs}>
                      <span className={styles.tabActive}>Login</span>
                    </div>
                  </header>

                  <form
                    className={styles.form}
                    onSubmit={(event) => {
                      event.preventDefault();
                      vm.submit();
                    }}
                  >
                    {vm.notice ? (
                      <LoginNoticeCard notice={vm.notice} remainingSeconds={vm.block.remainingSeconds} />
                    ) : null}

                    <FormField
                      label="Seu e-mail"
                      required
                      hideRequiredText
                      error={vm.form.formState.errors.email?.message}
                    >
                      {(props) => (
                        <Input
                          {...props}
                          {...vm.form.register('email')}
                          type="email"
                          autoComplete="email"
                          placeholder="seu@email.com.br"
                          variant="underline"
                          autoFocus
                        />
                      )}
                    </FormField>

                    <FormField
                      label="Sua senha"
                      required
                      hideRequiredText
                      error={vm.form.formState.errors.password?.message}
                    >
                      {(props) => (
                        <Input
                          {...props}
                          {...vm.form.register('password')}
                          type={senhaVisivel ? 'text' : 'password'}
                          autoComplete="current-password"
                          placeholder="sua senha"
                          variant="underline"
                          suffix={
                            <button
                              type="button"
                              className={styles.toggle}
                              onClick={() => setSenhaVisivel((visivel) => !visivel)}
                              aria-label={senhaVisivel ? 'Ocultar senha' : 'Mostrar senha'}
                              aria-pressed={senhaVisivel}
                            >
                              <Icon as={senhaVisivel ? EyeOff : Eye} size={16} />
                            </button>
                          }
                        />
                      )}
                    </FormField>

                    <button type="button" className={styles.forgot} onClick={vm.openForgotPassword}>
                      Esqueceu sua senha?
                    </button>

                    <div className={styles.submitRow}>
                      <Button
                        type="submit"
                        loading={vm.isSubmitting}
                        disabled={vm.block.isBlocked}
                        icon={<Icon as={ArrowRight} size={16} />}
                      >
                        Acessar
                      </Button>
                    </div>
                  </form>

                  <p className={styles.help}>Precisa de ajuda? Fale com o suporte.</p>
                </>
              )}
          </div>
        </section>
      </div>

      <ForgotPasswordModal
        open={vm.forgotPasswordOpen}
        onClose={vm.closeForgotPassword}
        defaultEmail={emailDigitado}
      />
    </main>
  );
}
